<template lang="html">
  <div>
    <div id="tooltip" />
    <div id="chart-placeholder" />
  </div>
</template>
<script>
  import Vue from 'vue';
  import filter from 'lodash/filter';
  import intersection from 'lodash/intersection';
  import { chordRdr, groupTip, chordKey, createMatrixAndMap } from './chordHelperFunctions';

  export default Vue.extend({
    data() {
      return {
        last_layout: '',
        show_empty_message: false,
        checked_names: [],
      };
    },
    props: {
      summary: {
        type: Array,
        default: [],
      },
    },
    methods: {
      updateFilteredIds: function() {
        return this.summary.filter((student, index) => this.checked_names[index]).map((student) => student.user_id);
      },
      showEmptyMessage: function() {
        this.$emit('showEmptyMessage');
      },
    },
    computed: {
      hasFilteredStudents() {
        return this.checked_names.includes(false);
      },
      is_private_page() {
        const routeName = this.$route.name;
        if (routeName === 'roundtable.feed') {
          return true;
        }
        return false;
      },
    },
    mounted() {
      const d3 = window.d3version3;
      // push all id's to make checkboxes marked as default
      this.summary.forEach((element, index) => {
        this.checked_names[index] = true;
      });
      const that = this;
      const screenSize = window.innerWidth;
      let width = 500;
      let height = 500;
      if (screenSize < 700) {
        width = screenSize - 50;
        height = screenSize - 50;
      }
      const outerRadius = Math.min((width, height) / 2 - 40);
      const innerRadius = outerRadius - 13;

      // create the arc
      const arc = d3.svg.arc().innerRadius(innerRadius).outerRadius(outerRadius);

      // create the chord path data generator for the chords
      const path = d3.svg.chord().radius(innerRadius);

      // define the default chord layout parameters
      function getDefaultLayout() {
        return d3.layout.chord().padding(0.03).sortSubgroups(d3.descending).sortChords(d3.ascending);
      }

      const fill = d3.scale.ordinal().range(['#A1E3EC', '#FFC085', '#0B162B', '#958058', '#EB2D7C', '#77D300', '#9FA0A2', '#FFF069', '#4A1E87', '#FF908D', '#4BB680', '#2C8E9C', '#FFD3AB', '#273248', '#412E0C', '#F581B2', '#89F003', '#C0B01E', '#9C79CD', '#FF6D69', '#158C4F', '#72CCD7', '#FF9E44', '#3F4B63', '#6C5935', '#C50053', '#C6F983', '#808284', '#FFF8B8', '#7952B1', '#C7221D', '#A7E7C7']);

      // ****** INITIALIZE THE CHART ******

      function updateChords(data) {
        const g = d3.select('#chart-placeholder').append('svg').attr('width', width).attr('height', height).append('g').attr('id', 'circle').attr('transform', `translate( ${width / 2} , ${height / 2} )`);

        // all coordinates will be relative to the center of the circle
        g.append('circle').attr('r', outerRadius);

        // filter the array(data) and returns the MATRIX and the MAP
        const matrixAndMap = createMatrixAndMap(data);
        const matrix = matrixAndMap.matrix;
        const mmap = matrixAndMap.map;
        const rdr = chordRdr(matrix, mmap);

        const layout = getDefaultLayout(); // create a new layout object
        layout.matrix(matrix);

        function chordTip(d) {
          const student1 = filter(mmap, { name: d.sname });
          const student2 = filter(mmap, { name: d.tname });
          student1 ? (d.sname = student1[0].data) : null;
          student2 ? (d.tname = student2[0].data) : null;
          return `Comments from ${d.sname} to ${d.tname}: ${d.svalue} <br/> Comments from ${d.tname} to ${d.sname}: ${d.tvalue} <br/>`;
        }

        function arcTween() {
          return (d) => {
            // create a zero-width arc object
            const emptyArc = {
              startAngle: d.startAngle,
              endAngle: d.startAngle,
            };
            const tween = d3.interpolate(emptyArc, d);
            // this function calculates the intermediary shapes
            return (t) => arc(tween(t));
          };
        }

        function chordTween() {
          return (d) => {
            // this function will be called for each active chord
            const emptyChord = {
              source: {
                startAngle: d.source.startAngle,
                endAngle: d.source.startAngle,
              },
              target: {
                startAngle: d.target.startAngle,
                endAngle: d.target.startAngle,
              },
            };
            const tween = d3.interpolate(emptyChord, d);
            // this function calculates the intermediary shapes
            return (t) => path(tween(t));
          };
        }

        const groupG = g.selectAll('g.group').data(layout.groups(), (d) => d.index);

        groupG.exit()
          .remove() // remove filtered groups before running transitions
          .transition()
          .duration(500)
          .attr('opacity', 0);

        const newGroups = groupG.enter().append('g').attr('class', 'group');

        // create the arc paths
        newGroups.append('path').attr('id', (d) => `group ${d.index}`).style('fill', (d) => fill(rdr(d).gname)).style('stroke', 'black');

        // update the paths to match the layout
        groupG
          .select('path')
          .transition()
          .duration(300)
          .attr('opacity', 0.5) // optional, just to observe the transition
          .attrTween('d', arcTween())
          .transition()
          .duration(100)
          .attr('opacity', 1);

        // create the group labels
        newGroups
          .append('svg:text')
          .transition()
          .duration(100)
          .each((d) => {
            d.angle = (d.startAngle + d.endAngle) / 2;
          })
          .attr('dy', '.35em')
          .attr('text-anchor', (d) => {
            d.angle > Math.PI ? 'end' : null;
          })
          .attr('transform', (d) => {
            `rotate(${(d.angle * 180) / Math.PI - 90})
             translate(${innerRadius + 26})
             ${d.angle > Math.PI ? 'rotate(180)' : 'rotate(0)'}`;
          })
          .text((d) => rdr(d).gdata);

        // position group labels to match layout
        groupG
          .select('text')
          .transition()
          .duration(300)
          .attr('transform', (d) => {
            d.angle = (d.startAngle + d.endAngle) / 2;
            return `rotate(${(d.angle * 180) / Math.PI - 90})
                        translate(${innerRadius + 26})
                        ${d.angle > Math.PI ? 'rotate(180)' : 'rotate(0)'}`;
          })
          .attr('text-anchor', (d) => (d.angle > Math.PI ? 'end' : 'begin'));

        // create/update the chord paths
        const chordPaths = g
          .selectAll('path.chord')
          .data(layout.chords(), chordKey);

        chordPaths.enter().append('path').attr('class', 'chord');

        // handle exiting paths:
        chordPaths
          .exit()
          .transition()
          .duration(800)
          .attr('opacity', 0)
          .remove();

        // update the path shape
        chordPaths
          .transition()
          .duration(500)
          .attr('opacity', 0.5)
          .style('fill', (d) => fill(rdr(d).sname))
          .style('stroke', (d) => {
            d3.rgb(fill(rdr(d).sname)).darker();
          })
          .attrTween('d', chordTween())
          .transition()
          .duration(100)
          .attr('opacity', 1);

        let positionLeft = 17;
        const is_modal = document.getElementsByClassName('modal-body');

        if (screenSize < 400) {
          positionLeft = 0;
        } else if (is_modal.length !== 0) {
          positionLeft = 20;
        }
        // logic for hovering paths
        // TODO: need to change this to arrow function but then "this" wont work, ask JC
        chordPaths.on('mouseover', function showTooltip(d) {
          d3.select('#tooltip')
            .style('visibility', 'visible')
            .html(chordTip(rdr(d)))
            .style('flex', 'justify-center')
            .style('right', () => positionLeft + '%');
          d3.selectAll('.chord').classed('opacity', true);
          d3.select(this).classed('opacity', false);
        });
        chordPaths.on('mouseout', () => {
          d3.select('#tooltip').style('visibility', 'hidden');
        });

        // logic for hovering groups
        groupG.on('mouseover', (d) => {
          d3.select('#tooltip')
            .style('visibility', 'visible')
            .html(groupTip(rdr(d)))
            .style('flex', 'justify-center')
            .style('right', () => positionLeft + '%');
          // hide paths from the other groups
          chordPaths.classed(
            'fade',
            (p) => p.source.index !== d.index && p.target.index !== d.index
          );
        });

        groupG.on('mouseout', () =>
          d3.select('#tooltip').style('visibility', 'hidden')
        );

        // NOTE: this is how we save the layout to be used after filtering a student
        that.last_layout = layout;
      }

      // create the chord if there is at least one relation between students comments
      const createChart = () => {
        const filteredIds = this.updateFilteredIds();
        const filteredStudents = this.summary.filter((student) =>
          filteredIds.includes(student.user_id)
        );
        let commentedTo = [];
        filteredStudents.forEach((e) => {
          commentedTo = [...commentedTo, ...e.users_I_have_commented_to];
        });
        if (intersection(commentedTo, filteredIds).length > 0) {
          // this.show_filter = true;
          updateChords(filteredStudents);
        } else {
          this.showEmptyMessage();
        }
      };

      document.addEventListener(
        'visibilitychange',
        handleVisibilityChange,
        false
      );

      function handleVisibilityChange() {
        if (document.visibilityState === 'hidden') {
          return null;
        } else {
          d3.select('#chart-placeholder').selectAll('svg').remove();
          createChart();
        }
      }

      createChart();

      d3.select('#filter-button').on('click', () => {
        d3.select('#chart-placeholder').selectAll('svg').remove();
        createChart();
      });
      // });
    },
  });
</script>

<style lang="scss">
  @keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  #chart-placeholder {
    height: 100%;
  }
  .filter-box {
    animation: fadein 1s;
    text-align: left;
    padding-top: 20px;
    padding-left: 35px;
  }
  .checkboxes-container {
    height: 30px;
  }
  #tooltip {
    color: white;
    font-size: 13px;
    opacity: 0.9;
    background: #333;
    padding: 5px;
    border: 1px solid lightgrey;
    border-radius: 5px;
    position: absolute;
    z-index: 10;
    visibility: hidden;
    white-space: nowrap;
    pointer-events: none;
  }
  form .btn-primary {
    margin-top: 25px;
  }
  #circle circle {
    fill: none;
    pointer-events: all;
  }
  path.chord {
    fill-opacity: 0.9;
    transition: opacity 0.3s;
  }
  #circle:hover path.fade {
    opacity: 0;
  }
  svg {
    font-size: 11px;
    color: #999;
  }
  #circle:hover path.fade {
    display: none;
  }
  #circle:hover path.opacity {
    opacity: 0.3;
    transition: opacity 200ms;
  }
</style>



// WEBPACK FOOTER //
// chordDiagram.vue?7b2cb43f